/**
 * 💳 StripePayment - Composant de paiement Stripe
 * 
 * Gère l'achat de couronnes et de cadeaux via Stripe Checkout
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, Crown, Gift, Loader2, Check, X, 
  Sparkles, ShoppingBag, Heart, AlertCircle 
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 💎 Packages de couronnes
const CROWN_PACKAGES = [
  { id: 'pack_100', name: 'Starter', crowns: 100, price: 4.99, bonus: 0 },
  { id: 'pack_300', name: 'Popular', crowns: 300, price: 12.99, bonus: 30, popular: true },
  { id: 'pack_500', name: 'Best Value', crowns: 500, price: 19.99, bonus: 75 },
  { id: 'pack_1000', name: 'VIP', crowns: 1000, price: 34.99, bonus: 200 },
];

// 🎁 Cadeaux boutique
const GIFT_PACKAGES = [
  { id: 'champagne_moet', name: 'Champagne Moët', price: 39.99, emoji: '🍾' },
  { id: 'champagne_veuve', name: 'Champagne Veuve Clicquot', price: 49.99, emoji: '🥂' },
  { id: 'vin_premium', name: 'Vin Rouge Premium', price: 24.99, emoji: '🍷' },
  { id: 'chocolat_box', name: 'Coffret Chocolats', price: 19.99, emoji: '🍫' },
  { id: 'roses_bouquet', name: 'Bouquet de Roses', price: 29.99, emoji: '🌹' },
];

/**
 * Composant principal de paiement Stripe
 */
export function StripePayment({ 
  customerId, 
  onClose, 
  onSuccess,
  defaultTab = 'crowns',
  isDarkMode = true 
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'pending', 'success', 'error'

  // Vérifier si on revient de Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentResult = urlParams.get('payment');

    if (sessionId && paymentResult === 'success') {
      setPaymentStatus('pending');
      pollPaymentStatus(sessionId);
      
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentResult === 'cancelled') {
      toast.info('Paiement annulé');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Polling du statut de paiement
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('error');
      toast.error('Timeout - Vérifiez votre email pour la confirmation');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/stripe/checkout/status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('🎉 Paiement réussi !');
        onSuccess?.();
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('error');
        toast.error('Session expirée');
        return;
      }

      // Continuer le polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Erreur polling:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  // Initier le paiement
  const handlePayment = async () => {
    if (!selectedPackage) {
      toast.error('Sélectionnez un pack');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/stripe/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: selectedPackage.id,
          package_type: activeTab,
          customer_id: customerId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Erreur création session');
      }

      const data = await response.json();
      
      // Rediriger vers Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Erreur paiement:', error);
      toast.error('Erreur lors du paiement');
      setIsLoading(false);
    }
  };

  // Écran de statut de paiement
  if (paymentStatus) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDarkMode ? 'bg-black/80' : 'bg-white/80'}`}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`max-w-md w-full rounded-2xl p-8 text-center ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}
        >
          {paymentStatus === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-purple-500 animate-spin mb-4" />
              <h3 className="text-xl font-bold mb-2">Vérification du paiement...</h3>
              <p className="text-gray-500">Merci de patienter</p>
            </>
          )}
          
          {paymentStatus === 'success' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 mx-auto bg-green-500 rounded-full flex items-center justify-center mb-4"
              >
                <Check className="w-10 h-10 text-white" />
              </motion.div>
              <h3 className="text-xl font-bold mb-2 text-green-500">Paiement réussi ! 🎉</h3>
              <p className="text-gray-500 mb-6">Vos couronnes ont été ajoutées</p>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold"
              >
                Super !
              </button>
            </>
          )}
          
          {paymentStatus === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto bg-red-500 rounded-full flex items-center justify-center mb-4">
                <X className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-red-500">Erreur de paiement</h3>
              <p className="text-gray-500 mb-6">Vérifiez votre email ou réessayez</p>
              <button
                onClick={() => setPaymentStatus(null)}
                className="px-6 py-3 bg-gray-700 text-white rounded-xl font-bold"
              >
                Réessayer
              </button>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDarkMode ? 'bg-black/80' : 'bg-white/80'}`}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={`max-w-lg w-full rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8" />
              <div>
                <h2 className="text-xl font-bold">Boutique</h2>
                <p className="text-white/70 text-sm">Paiement sécurisé Stripe</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => { setActiveTab('crowns'); setSelectedPackage(null); }}
            className={`flex-1 py-4 flex items-center justify-center gap-2 font-medium transition ${
              activeTab === 'crowns' 
                ? 'text-yellow-400 border-b-2 border-yellow-400' 
                : 'text-gray-500'
            }`}
          >
            <Crown className="w-5 h-5" />
            Couronnes
          </button>
          <button
            onClick={() => { setActiveTab('gift'); setSelectedPackage(null); }}
            className={`flex-1 py-4 flex items-center justify-center gap-2 font-medium transition ${
              activeTab === 'gift' 
                ? 'text-pink-400 border-b-2 border-pink-400' 
                : 'text-gray-500'
            }`}
          >
            <Gift className="w-5 h-5" />
            Cadeaux
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {activeTab === 'crowns' && (
            <div className="grid grid-cols-2 gap-3">
              {CROWN_PACKAGES.map((pkg) => (
                <motion.button
                  key={pkg.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedPackage(pkg)}
                  className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                    selectedPackage?.id === pkg.id
                      ? 'border-yellow-400 bg-yellow-400/10'
                      : isDarkMode 
                        ? 'border-gray-700 hover:border-gray-600' 
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {pkg.popular && (
                    <span className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                      ⭐ Top
                    </span>
                  )}
                  
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-yellow-400" />
                    <span className="font-bold text-lg">{pkg.crowns}</span>
                  </div>
                  
                  {pkg.bonus > 0 && (
                    <div className="text-green-400 text-sm mb-2">
                      +{pkg.bonus} bonus !
                    </div>
                  )}
                  
                  <div className="text-xl font-bold">
                    {pkg.price.toFixed(2)} €
                  </div>
                </motion.button>
              ))}
            </div>
          )}

          {activeTab === 'gift' && (
            <div className="space-y-3">
              {GIFT_PACKAGES.map((gift) => (
                <motion.button
                  key={gift.id}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => setSelectedPackage(gift)}
                  className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                    selectedPackage?.id === gift.id
                      ? 'border-pink-400 bg-pink-400/10'
                      : isDarkMode 
                        ? 'border-gray-700 hover:border-gray-600' 
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-4xl">{gift.emoji}</span>
                  <div className="flex-1 text-left">
                    <div className="font-bold">{gift.name}</div>
                    <div className="text-sm text-gray-500">À récupérer en boutique</div>
                  </div>
                  <div className="text-xl font-bold text-pink-400">
                    {gift.price.toFixed(2)} €
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>

        {/* Footer - CTA */}
        <div className="p-6 border-t border-gray-700">
          <button
            onClick={handlePayment}
            disabled={!selectedPackage || isLoading}
            className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition ${
              selectedPackage && !isLoading
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Redirection vers Stripe...
              </>
            ) : selectedPackage ? (
              <>
                <CreditCard className="w-5 h-5" />
                Payer {selectedPackage.price.toFixed(2)} €
              </>
            ) : (
              'Sélectionnez un article'
            )}
          </button>
          
          <div className="mt-4 flex items-center justify-center gap-2 text-gray-500 text-sm">
            <AlertCircle className="w-4 h-4" />
            Paiement sécurisé par Stripe
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Bouton pour ouvrir la boutique
 */
export function ShopButton({ onClick, isDarkMode = true }) {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-full font-bold shadow-lg"
    >
      <ShoppingBag className="w-5 h-5" />
      Boutique
    </motion.button>
  );
}

export default StripePayment;
